import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ── Rank chip colour maps (static for Tailwind purge safety) ────────────────
export const RANK_ACTIVE = {
    S: 'bg-amber-400 text-white border-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.5)]',
    A: 'bg-rose-400  text-white border-rose-400 shadow-[0_0_8px_rgba(251,113,133,0.5)]',
    B: 'bg-pink-400  text-white border-pink-400 shadow-[0_0_8px_rgba(244,114,182,0.5)]',
    C: 'bg-emerald-500 text-white border-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]',
    D: 'bg-sky-400   text-white border-sky-400 shadow-[0_0_8px_rgba(56,189,248,0.5)]',
    E: 'bg-purple-400 text-white border-purple-400 shadow-[0_0_8px_rgba(192,132,252,0.5)]',
    F: 'bg-gray-400 text-white border-gray-400 shadow-[0_0_8px_rgba(156,163,175,0.5)]',
    G: 'bg-zinc-500 text-white border-zinc-500 shadow-[0_0_8px_rgba(113,113,122,0.5)]',
};

export const FILTER_RANKS = ['S', 'A', 'B', 'C', 'D', 'E', 'F', 'G'];

export const FILTER_CATEGORIES = [
    {
        id: 'surface', label: 'Surface',
        color: '#FAB0C0',
        items: [
            { key: 'turf',  label: 'Turf' },
            { key: 'dirt',  label: 'Dirt' },
        ],
    },
    {
        id: 'distance', label: 'Distance',
        color: '#48E5B9',
        items: [
            { key: 'short_distance',  label: 'Sprint' },
            { key: 'mile',            label: 'Mile'   },
            { key: 'medium_distance', label: 'Medium' },
            { key: 'long_distance',   label: 'Long'   },
        ],
    },
    {
        id: 'style', label: 'Style',
        color: '#a0a0b0',
        items: [
            { key: 'runner',    label: 'Front'  },
            { key: 'leader',    label: 'Pacer'  },
            { key: 'betweener', label: 'Late'   },
            { key: 'chaser',    label: 'End'    },
        ],
    },
];

export const FilterRow = ({ item, currentFilter, onSelect }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const isActive = !!currentFilter;
    const currentRank = isActive ? (typeof currentFilter === 'object' ? currentFilter.rank : currentFilter) : null;
    const currentOp = isActive ? (typeof currentFilter === 'object' ? currentFilter.op : '>=') : '>=';

    const handleRankSelect = (r) => {
        setIsOpen(false);
        if (r === null || (r === currentRank && !r)) {
            onSelect(item.key, null);
        } else {
            onSelect(item.key, { rank: r, op: currentOp });
        }
    };

    const nextOpMap = { '>=': '==', '==': '<=', '<=': '>=' };
    const opDisplayMap = { '>=': '≥', '==': '=', '<=': '≤' };
    const opTooltipMap = { '>=': 'Mayor o igual', '==': 'Buscar exactamente', '<=': 'Menor o igual' };
    
    const handleOpToggle = (e) => {
        e.stopPropagation();
        if (!currentRank) return;
        onSelect(item.key, { rank: currentRank, op: nextOpMap[currentOp] });
    };

    // Close dropdown securely
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    return (
        <div className="flex items-center gap-2" ref={dropdownRef}>
            <span className="w-12 text-[9px] font-black uppercase tracking-wider text-gray-400 dark:text-gray-500 shrink-0">
                {item.label}
            </span>
            
            <div className="flex flex-1 relative items-center gap-1.5 h-[28px] sm:h-[32px]">
                {/* Botón Principal del Dropdown */}
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className={`flex-1 flex items-center justify-between px-3 h-full rounded-lg border transition-all duration-300 outline-none hover:shadow-lg active:scale-[0.98]
                        ${currentRank 
                            ? 'bg-black/5 dark:bg-white/5 border-[#48E5B9]/30 text-slate-800 dark:text-white shadow-[0_0_15px_rgba(72,229,185,0.05)]' 
                            : 'bg-black/[0.02] dark:bg-white/[0.02] border-black/10 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:border-black/30 dark:hover:border-white/30 hover:bg-black/[0.04] dark:hover:bg-white/[0.04]'
                        }
                    `}
                >
                    <div className="flex items-center gap-2">
                        {currentRank ? (
                            <>
                                <span className={`w-4 h-4 rounded-full text-[8px] flex items-center justify-center font-black ${RANK_ACTIVE[currentRank]}`}>
                                    {currentRank}
                                </span>
                                <span className="text-[11px] font-black tracking-widest text-[#48E5B9]">RANGO</span>
                            </>
                        ) : (
                            <span className="text-[10px] font-semibold opacity-50 tracking-wider">CUALQUIERA</span>
                        )}
                    </div>
                    
                    <motion.svg 
                        animate={{ rotate: isOpen ? 180 : 0 }} 
                        className={`w-3.5 h-3.5 transition-colors ${currentRank ? 'text-[#48E5B9]' : 'text-gray-400 dark:text-gray-500'}`} 
                        fill="none" viewBox="0 0 24 24" stroke="currentColor"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                    </motion.svg>
                </button>

                {/* Operator Toggle */}
                <AnimatePresence>
                    {currentRank && (
                        <motion.button 
                            initial={{ opacity: 0, scale: 0.5, width: 0 }}
                            animate={{ opacity: 1, scale: 1, width: '32px' }}
                            exit={{ opacity: 0, scale: 0.5, width: 0 }}
                            onClick={handleOpToggle}
                            title={opTooltipMap[currentOp]}
                            className="shrink-0 h-full flex items-center justify-center rounded-lg bg-white dark:bg-[#0B0F19] text-[#FAB0C0] border border-[#FAB0C0]/30 font-black text-[13px] hover:bg-[#FAB0C0]/10 hover:border-[#FAB0C0]/60 active:scale-90 transition-all shadow-[0_0_15px_rgba(250,176,192,0.15)]"
                        >
                            {opDisplayMap[currentOp]}
                        </motion.button>
                    )}
                </AnimatePresence>

                {/* Dropdown Menu (Glassmorphism Masterpiece) */}
                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            initial={{ opacity: 0, y: 8, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 8, scale: 0.95 }}
                            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                            className="absolute top-[calc(100%+6px)] left-0 w-[calc(100%+10px)] min-w-[200px] z-[100] rounded-[16px] p-2 backdrop-blur-3xl bg-white/90 dark:bg-[#0B0F19]/90 border border-black/10 dark:border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.1)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.6)] origin-top-left overflow-hidden"
                        >
                            {/* Inner ambient glow */}
                            <div className="absolute inset-0 bg-gradient-to-b from-black/5 dark:from-white/5 to-transparent pointer-events-none" />
                            
                            <button
                                onClick={() => handleRankSelect(null)}
                                className="relative w-full text-left px-3 py-2.5 rounded-xl text-[10px] font-bold text-gray-500 dark:text-gray-400 hover:bg-black/[0.06] dark:hover:bg-white/[0.06] hover:text-slate-900 dark:hover:text-white transition-colors flex items-center justify-between group"
                            >
                                <span>Cualquiera (Limpiar)</span>
                                <span className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-500 group-hover:bg-slate-800 dark:group-hover:bg-white transition-colors" />
                            </button>
                            
                            <div className="h-px w-full bg-black/5 dark:bg-white/5 my-2" />
                            
                            <div className="grid grid-cols-4 gap-1.5 relative">
                                {FILTER_RANKS.map(rank => {
                                    const isSelected = currentRank === rank;
                                    return (
                                        <button
                                            key={rank}
                                            onClick={() => handleRankSelect(rank)}
                                            className={`
                                                relative flex items-center justify-center h-9 rounded-xl text-[12px] font-black transition-all border
                                                ${isSelected 
                                                    ? RANK_ACTIVE[rank]
                                                    : 'bg-black/[0.02] dark:bg-white/[0.02] border-black/5 dark:border-white/5 text-gray-500 dark:text-gray-400 hover:bg-black/5 dark:hover:bg-white/10 hover:border-black/10 dark:hover:border-white/20 hover:text-slate-900 dark:hover:text-white'
                                                }
                                            `}
                                        >
                                            {rank}
                                            {isSelected && (
                                                <motion.div 
                                                    layoutId="rankHighlightIndicator"
                                                    className="absolute inset-0 rounded-xl border-2 border-black/20 dark:border-white/30"
                                                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                                />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

const RacePlannerFilter = ({ filters, onFilterChange }) => {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 relative z-30">
            {FILTER_CATEGORIES.map(cat => (
                <div key={cat.id} className="space-y-4">
                    <h4 className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.4em] text-white/50">
                        <span className="w-1.5 h-1.5 rounded-full drop-shadow-[0_0_5px_currentColor]" style={{ backgroundColor: cat.color, color: cat.color }} />
                        {cat.label}
                    </h4>
                    <div className="space-y-3 relative">
                        {cat.items.map(item => (
                            <FilterRow
                                key={item.key}
                                item={item}
                                currentFilter={filters[item.key]}
                                onSelect={onFilterChange}
                            />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default RacePlannerFilter;
